import axios from 'axios';
import {useContext, useEffect, useRef, useState} from 'react';
import useForm from '../../../hooks/useForm';
import {isEmpty} from 'lodash';
import {checkAndFormatDates} from '../../../utils/dateHelpers';
import AddItemBox from '../../shared/AddItemBox';
import {Flex, Stack} from '../../shared/layouts';
import ProfileBlock from './ProfileBlock';
import Button from '../../shared/Button';
import MessagebarContext from '../../../context/MessagebarContext';
import UserContext from '../../../context/UserContext';
import PreventUnload from '../../shared/PreventUnload';
import Loader from '../../shared/Loader';
import WhiteLabelContext from '../../../context/WhiteLabelContext';
import {useTranslation} from "react-i18next";
import AddressesBlock from "./AddressesBlock";
import ContactAddressBlock from "./ContactAddressBlock";
import ReportIncorrectDataLink from "../../shared/ReportIncorrectDataLink";
import CompanyProfile from "./company/CompanyProfile";
//import Header from '../../shared/Header';
//import PersonSection from './PersonSection';

//const personInfo = {
//  firstname: '',
//  initials: '',
//  addition: '',
//  lastname: '',
//  date_of_birth: '',
//  gender: undefined,
//};

const ProfilePage = () => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  const [applicantData, changeApplicantData, setApplicantData] = useForm({});
  const [companyData, setCompanyData] = useState(null);
  const [partnerData, changePartnerData, setPartnerData] = useForm({});
  const [hasPartner, setHasPartner] = useState(false);
  const [children, setChildren] = useState([]);
  const [contactAddressData, changeContactAddressData, setContactAddressData] =
    useForm({});
  const [residenceData, setResidenceData] = useState({});
  const [addressesWithType, setAddressesWithType] = useState([]);

  const [applicantErrors, setApplicantErrors] = useState({});
  const [partnerErrors, setPartnerErrors] = useState({});
  const [contactAddressErrors, setContactAddressErrors] = useState({});

  const { setSuccesMessage, setAlertMessage } = useContext(MessagebarContext);
  const { setChangedDataWithoutSaving, user } = useContext(UserContext);
  const { canEditProfile } = useContext(WhiteLabelContext);
  const mountedRef = useRef(true);

  useEffect(() => {
    setChangedDataWithoutSaving(false);
  }, []);

  useEffect(() => {
    Promise.all([axios.get('/dossier/profile'), axios.get('/dossier/address')])
      .then(([{ data: profileData }, { data: addressData }]) => {
        if (!mountedRef.current) return null;
        const { applicant, contact_address, partner, children, addresses_with_type, company } = profileData;
        const { address } = addressData;
        const hasPartnerData = Boolean(partner);

        setHasPartner(hasPartnerData);
        setApplicantData({ ...checkAndFormatDates(applicant) });
        if (hasPartnerData) setPartnerData(checkAndFormatDates(partner));
        setContactAddressData(checkAndFormatDates(contact_address || {}));
        setResidenceData(checkAndFormatDates(address));
        setChildren(checkAndFormatDates(children));
        setAddressesWithType(checkAndFormatDates(addresses_with_type));
        setCompanyData(company);
      })
      .catch((err) => {
        setAlertMessage();
        console.error(err);
      })
      .finally(() => {
        setIsLoading(false);
      });

    return () => {
      mountedRef.current = false;
    };
  }, []);

  const saveProfile = (e) => {
    e.preventDefault();
    // TODO: add children when backend is ready
    setIsLoading(true);
    axios
      .post('/dossier/profile', {
        applicant: checkAndFormatDates(applicantData, true),
        partner: hasPartner && Object.keys(partnerData).length > 0
          ? checkAndFormatDates(partnerData, true)
          : null,
        contact_address: checkAndFormatDates(contactAddressData, true),
      })
      .then(() => {
        setSuccesMessage();
        setPartnerErrors({});
        setApplicantErrors({});
        setContactAddressErrors({});
        setChangedDataWithoutSaving(false);
        setIsLoading(false);
      })
      .catch((err) => {
        setAlertMessage();
        setIsLoading(false);
        Object.entries(err.response.data.errors).forEach(([key, value]) => {
          if (key.includes('applicant')) {
            setApplicantErrors((errors) => ({
              ...errors,
              [key.replace(/^[^.]*[.]/, '')]: value,
            }));
          }
          if (key.includes('partner')) {
            setPartnerErrors((errors) => ({
              ...errors,
              [key.replace(/^[^.]*[.]/, '')]: value,
            }));
          } else {
            setContactAddressErrors((errors) => ({
              ...errors,
              [key.replace(/^[^.]*[.]/, '')]: value,
            }));
          }
        });
      });
  };

  const ConsumerProfile = () => (
    <Stack
      as="form"
      onSubmit={saveProfile}
      space="xl"
      width={{ sm: 656, ml: 672, lg: 800, xl: 1024 }}
    >

      <ProfileBlock
        {...{
          ...applicantData,
          handleChange: changeApplicantData,
          errors: applicantErrors,
          showSaveBtn: true,
        }}
      />

      {(!canEditProfile ? (!isEmpty(contactAddressData)) : true) && (
        <ContactAddressBlock
          {...{
            contactAddressData,
            residenceData,
            handleChange: changeContactAddressData,
            errors: contactAddressErrors,
            readOnly: !canEditProfile,
          }}
        />
      )}

      {!isEmpty(addressesWithType) && (
        <AddressesBlock addresses={addressesWithType} />
      )}

      {hasPartner && (
        <ProfileBlock
          partner
          {...{
            ...partnerData,
            handleChange: changePartnerData,
            errors: partnerErrors,
            onRemove: () => {
              setHasPartner(false)
            },
          }}
        />
      )}
      {!hasPartner && canEditProfile && (
        <AddItemBox
          title={t('Partner toevoegen')}
          onClick={() =>
            setHasPartner(true)
          }
        />
      )}
      {children.map((childData, index) => (
        <ProfileBlock key={`child-${index}`} child {...childData} />
      ))}
      {!canEditProfile && (
        <ReportIncorrectDataLink />
      )}
      {canEditProfile && (
        <Flex justifyContent="flex-end">
          <Button type="submit">{t('Opslaan')}</Button>
        </Flex>
      )}
    </Stack>
  );

  return (
    <PreventUnload>
      {isLoading && <Loader />}
      { user?.business_account ? (
        <CompanyProfile companyData={companyData} addressesWithType={addressesWithType} applicantData={applicantData} />
      ) : (
        <ConsumerProfile />
      )}

    </PreventUnload>
  );
};

export default ProfilePage;
